import { headingColor, headingXs } from '@insify/design-system/typography';
import { makeGoogleMapsRequesters } from '@insify/frontend-google-maps';
import { AddressBlock } from '@insify/funnel-components';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import styles from './CompanyAddressForm.module.css';

import type { FieldError } from 'react-hook-form';

const { requestAddressDetail, requestAutocomplete } = makeGoogleMapsRequesters({
  language: 'fr',
  // We need 11 countries but the API only allows only 5: https://insify.slack.com/archives/C03HT2JTQRL/p1683289540297609?thread_ts=1683284956.713509&cid=C03HT2JTQRL
  countryRestrictions: ['fr', 'gp', 'mq', 're', 'gf'],
  apiKey: window.insify_runtime_config.GOOGLE_MAPS_API_KEY,
});

const requestAddress = ({ placeId }: { placeId: string }) =>
  requestAddressDetail({ placeId }, () => Sentry.captureException('postal code is missing from geoLocation'));

export const CompanyAddressForm = ({
  error,
  onAddressReset,
  className,
}: {
  error?: FieldError;
  onAddressReset?: () => void;
  className?: string;
}): JSX.Element => {
  const useFormReturn = useFormContext();
  const { t } = useTranslation(['funnel', 'common', 'validation']);

  return (
    <div className={className}>
      <h3 className={clsx(headingXs, headingColor)}>{t('funnel:address_company_address')}</h3>
      <AddressBlock
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        useFormReturn={useFormReturn as any}
        className={styles.addressBlock}
        requestAutocomplete={requestAutocomplete}
        requestAddressDetails={requestAddress}
        shouldShowStreetAddition={true}
        onAddressReset={onAddressReset}
        error={error}
        translations={{
          addressSearchLabel: t('funnel:address_search_label'),
          addressSearchPlaceholder: t('funnel:address_search_placeholder'),
          addressSearchError: t('validation:address_search_error'),
          houseNumberLabel: t('funnel:address_form_house_number'),
          streetLabel: t('funnel:address_form_street'),
          houseNumberAdditionLabel: t('funnel:address_form_street_addition'),
          houseNumberAdditionPlaceholder: t('funnel:address_form_street_addition_placeholder'),
          optional: t('common:optional'),
          postalCodeLabel: t('funnel:address_form_postalcode'),
          cityLabel: t('funnel:address_form_city'),
        }}
      />
    </div>
  );
};
